import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { DateInput } from '@/common/components/DateInput/DateInput'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import ResumeItem from '@/common/components/ResumeItem'
import { ShowHeader } from '@/common/components/ShowHeader'
import TextField from '@/common/components/TextField'
import { UnitTextField } from '@/common/components/UnitTextField'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { formatCurrency } from '@/common/utils/formatters'
import { FormTransformers } from '@/common/utils/formTransformers'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ClockIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useMaintenanceForwarding } from '../../../../hooks/forwardings/useMaintenanceForwarding'
import { MaintenanceForwardingsDirectiveEntryKlass } from '../../../../types/forwardings/maintenanceForwardingsDirectiveEntryKlasses.type'
import { useUpdateMaintenanceExecutionEntryEstablishmentAccept } from '../../hooks/useUpdateMaintenanceExecutionEntryEstablishmentAccept'
import {
  updateExecutionEntryEstablishmentAcceptSchema,
  UpdateExecutionEntryEstablishmentAcceptValidationSchema,
} from '../../schemas/updateExecutionEntryEstablishmentAccept.schema'
import { updateExecutionEntryErrorsTransformer } from '../../utils/updateExecutionEntryErrorsTransformer'
import MaintenanceWorkflowExecutionEntryUpdateLoading from './components/MaintenanceWorkflowExecutionEntryUpdateLoading'
import ExecutionEntryUpdateObjectiveAttributes from './components/sections/ObjectiveAttributes'

const MaintenanceWorkflowExecutionEntryUpdate = () => {
  const { workflowId, forwardingId } = useParams()

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const queryClient = useQueryClient()

  const goBack = useGoBack()

  const [formDisabled, setFormDisabled] = useState(false)

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    data: forwarding,
    isPending: forwardingIsPending,
    error: forwardingError = {} as any,
    refetch: refetchForwarding,
    dataUpdatedAt: forwardingUpdatedAt,
  } = useMaintenanceForwarding<MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry>(forwardingId)

  const formMethods = useForm<UpdateExecutionEntryEstablishmentAcceptValidationSchema>({
    resolver: zodResolver(updateExecutionEntryEstablishmentAcceptSchema),
    disabled: formDisabled,
  })

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = formMethods

  const { mutate: establishmentAccept, isPending: establishmentAcceptIsPending } =
    useUpdateMaintenanceExecutionEntryEstablishmentAccept({
      establishmentChoiceEntryId: forwarding?.establishment_choice_entry?.actual_proposal?.id,
      options: {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['maintenanceWorkflow', workflowId] })
          queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding', forwardingId] })

          goBack({
            fallback: `/maintenance-workflows/${workflowId}`,
            searchLastRouteFromFallback: true,
          })
        },
        onError: (error) => {
          FormTransformers.errorsTransformer({
            setError,
            setMessages,
            transformer: updateExecutionEntryErrorsTransformer,
          })(error)
        },
      },
    })

  const onSubmit: SubmitHandler<UpdateExecutionEntryEstablishmentAcceptValidationSchema> = (data) =>
    establishmentAccept(data)

  const isLoading = useMemo(() => {
    return isUserPermissionsPending || forwardingIsPending
  }, [isUserPermissionsPending, forwardingIsPending])

  const hasPermissionToUpdate = useMemo(() => {
    return hasUserPermission(
      'Establishment::Maintenance::EstablishmentChoice::ManualEntryProposals::EstablishmentAccept',
    )
  }, [isUserPermissionsPending])

  useEffect(() => {
    if (forwardingIsPending) {
      return
    }

    const pieces = forwarding?.directive_entry?.objectives?.flatMap(
      (objective) =>
        objective.execution_pieces?.map((piece) => ({
          id: piece.id,
          original_unit_price: piece.unit_price,
          original_warranty_in_days: piece.warranty_in_days,
          new_unit_price: undefined,
          new_warranty_in_days: undefined,
        })) || [],
    )

    const activities = forwarding?.directive_entry?.objectives?.flatMap(
      (objective) =>
        objective.execution_activities?.map((activity) => ({
          id: activity.id,
          original_unit_price: activity.unit_price,
          original_warranty_in_days: activity.warranty_in_days,
          new_unit_price: undefined,
          new_warranty_in_days: undefined,
        })) || [],
    )

    reset({
      start_at_date: undefined,
      start_at_time: undefined,
      deadline_to_complete_in_days: undefined,
      pieces,
      activities,
    })
  }, [forwardingIsPending])

  useEffect(() => {
    setFormDisabled(establishmentAcceptIsPending)
  }, [establishmentAcceptIsPending])

  return (
    <FormProvider {...formMethods}>
      <div className="inner-screen">
        <ShowHeader
          goBack
          goBackProps={{
            fallback: `/maintenance-workflows/${workflowId}`,
            searchLastRouteFromFallback: true,
          }}
          isLoading={isLoading}
          title="Lance para execução da manutenção"
          titleClassName="text-lg font-medium"
          buttons={
            !isLoading &&
            hasPermissionToUpdate && (
              <Button onClick={handleSubmit(onSubmit)} isLoading={establishmentAcceptIsPending}>
                Enviar lance para execução
              </Button>
            )
          }
        />

        {isLoading && <MaintenanceWorkflowExecutionEntryUpdateLoading />}

        {!isLoading && (
          <ErrorMessageWrapper
            className="flex-1"
            statusCode={!hasPermissionToUpdate ? 403 : forwardingError?.status}
            refetchFunction={refetchForwarding}
          >
            <div className="flex-1 overflow-auto">
              <Box className="p-4 flex flex-col gap-4">
                <p className="text-lg font-medium">Edite os orçamentos por Objeto de manutenção</p>

                <div className="flex flex-col gap-2">
                  <p className="text-sm font-medium">Dados do diagnóstico</p>

                  <div className="flex gap-3 pb-3 border-b border-white-200">
                    <ResumeItem
                      className="text-sm"
                      label="Valor total"
                      value={
                        forwarding?.directive_entry?.diagnostic?.total_value
                          ? formatCurrency(Number(forwarding.directive_entry.diagnostic.total_value))
                          : '--'
                      }
                    />

                    <ResumeItem
                      className="text-sm"
                      label="Prazo em dias"
                      value={
                        forwarding?.directive_entry?.diagnostic?.deadline_to_complete_in_days != null
                          ? `${forwarding.directive_entry.diagnostic.deadline_to_complete_in_days} dia(s)`
                          : '--'
                      }
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <DateInput
                    fieldName="start_at_date"
                    control={control}
                    label="Data sugerida"
                    className="flex-1"
                    markAsRequired
                    errorMessage={errors.start_at_date?.message}
                  />

                  <Controller
                    control={control}
                    name="start_at_time"
                    render={({ field }) => (
                      <TextField
                        label="Horário sugerido"
                        type="time"
                        markAsRequired
                        className="flex-1"
                        LeftIcon={<ClockIcon className="w-5 h-5 text-white-400" />}
                        {...field}
                        value={field.value || ''}
                        errorMessage={errors.start_at_time?.message}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="deadline_to_complete_in_days"
                    render={({ field }) => (
                      <UnitTextField
                        decimals={0}
                        placeholder="Escreva aqui o prazo em dias"
                        unit="dia(s)"
                        label="Prazo em dias para execução"
                        markAsRequired
                        className="flex-1"
                        {...field}
                        value={field.value || ''}
                        errorMessage={errors.deadline_to_complete_in_days?.message}
                      />
                    )}
                  />
                </div>

                <ExecutionEntryUpdateObjectiveAttributes forwarding={forwarding} dataUpdatedAt={forwardingUpdatedAt} />
              </Box>
            </div>
          </ErrorMessageWrapper>
        )}

        <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
      </div>
    </FormProvider>
  )
}

export default MaintenanceWorkflowExecutionEntryUpdate
